import { useState } from 'react';
import { toast } from 'sonner';
import {
  Bell,
  Clock,
  FileText,
  MapPin,
  Pencil,
  Trash2,
} from 'lucide-react';

import { deleteCalendarEvent, type CalendarEvent } from '../api/client';
import { notifyMutationError } from '@/lib/errors';
import { SEM_HORA } from '@/lib/eventoHorario';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { eventTypeColors as themeEventTypeColors, EVENT_FALLBACK_COLOR } from '@/constants/calendar';
import { EventFormContent } from './EventFormDialog';
import { DaAgendaDe } from './calendario/DaAgendaDe';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { LinkDosAutos } from '@/components/processos/LinkDosAutos';

interface EventDetailDialogProps {
  event: CalendarEvent | null;
  onClose: () => void;
  onOpenComunicacao?: (comunicacaoId: string) => void;
}

const eventTypeColorMap: Record<string, string> = Object.fromEntries(
  Object.entries(themeEventTypeColors).map(([k, v]) => [k, v.color]),
);

function formatTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function EventDetailDialog({ event, onClose, onOpenComunicacao }: EventDetailDialogProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [formRevision, setFormRevision] = useState(0);

  const deleteMutation = useOfflineMutation({
    mutationFn: deleteCalendarEvent,
    outbox: {
      type: 'evento_deletar',
      getPayload: (id) => ({ method: 'DELETE' as const, url: `/calendario/eventos/${id}` }),
      getDescription: () => 'Excluir evento do calendário',
    },
    invalidateKeys: [[...queryKeys.calendario.eventos], [...queryKeys.calendario.agenda], [...queryKeys.processos.all], [...queryKeys.dashboard.regua]],
    onSuccess: () => {
      toast.success('Evento excluído');
      handleClose();
    },
    onQueued: () => {
      handleClose();
    },
    onError: (err) => notifyMutationError(err, toast.error),
  });

  const handleClose = () => {
    onClose();
    setMode('view');
    setConfirmDelete(false);
  };

  const handleDelete = () => {
    if (!event) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteMutation.mutate(event.id);
  };

  const switchToEdit = () => {
    setFormRevision((r) => r + 1);
    setMode('edit');
  };

  return (
    <Dialog open={!!event} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        {event && mode === 'view' && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <Badge
                  className="text-[11px] font-semibold border-transparent"
                  style={{
                    backgroundColor: `${eventTypeColorMap[event.tipo] || EVENT_FALLBACK_COLOR}20`,
                    color: eventTypeColorMap[event.tipo] || EVENT_FALLBACK_COLOR,
                  }}
                >
                  {(themeEventTypeColors as Record<string, { label: string; color: string }>)[event.tipo]?.label ?? event.tipo}
                </Badge>
              </div>
              <DialogTitle>{event.titulo}</DialogTitle>
              <DaAgendaDe evento={event} className="text-sm" />
            </DialogHeader>

            <div className="space-y-3 text-sm">
              {/* Data e hora */}
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" />
                <span>
                  {(() => {
                    const inicio = new Date(event.data_inicio);
                    const fim = event.data_fim ? new Date(event.data_fim) : null;
                    const fmtData = (d: Date) => d.toLocaleDateString('pt-BR', {
                      weekday: 'long', day: 'numeric', month: 'long',
                    });
                    const sameDay = fim && inicio.toDateString() === fim.toDateString();
                    if (!fim) {
                      // A intimação não disse a hora: a meia-noite é só a âncora do dia.
                      if (event.hora_conhecida === false) return `${fmtData(inicio)} — ${SEM_HORA}`;
                      return `${fmtData(inicio)} — ${formatTime(event.data_inicio)}`;
                    }
                    if (sameDay) {
                      return `${fmtData(inicio)} — ${formatTime(event.data_inicio)} até ${formatTime(event.data_fim!)}`;
                    }
                    return `${fmtData(inicio)} ${formatTime(event.data_inicio)} até ${fmtData(fim)} ${formatTime(event.data_fim!)}`;
                  })()}
                </span>
              </div>

              {/* Local */}
              {event.local && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span>{event.local}</span>
                </div>
              )}

              {/* Descrição com links para processo e comunicação */}
              {(event.descricao || event.processo_numero) && <EventDescription event={event} onOpenComunicacao={(id) => {
                onOpenComunicacao?.(id);
                handleClose();
              }} />}
            </div>

            {/* Ações */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="destructive"
                size="sm"
                className="flex-1"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                {confirmDelete ? 'Confirmar' : 'Excluir'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={switchToEdit}
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Editar
              </Button>
            </div>
          </>
        )}

        {event && mode === 'edit' && (
          <EventFormContent
            key={`${event.id}-${formRevision}`}
            event={event}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: structured description with links
// ---------------------------------------------------------------------------

function EventDescription({
  event,
  onOpenComunicacao,
}: {
  event: CalendarEvent;
  onOpenComunicacao: (id: string) => void;
}) {
  const lines = (event.descricao ?? '').split('\n');
  const processoMatch = lines[0]?.match(/^Processo:\s*(.+)$/);
  // O vínculo do evento vale mais que a linha do texto (o evento criado à mão não a tem).
  const processoNumero = event.processo_numero || processoMatch?.[1].trim();
  const comLine = lines.find((l) => /^Comunicação de\s+/.test(l));
  const classe = event.processo_classe || (event.titulo.includes(' — ')
    ? event.titulo.split(' — ').slice(1).join(' — ').trim()
    : undefined);
  const hasLinks = processoNumero || (comLine && event.comunicacao_id);

  if (!hasLinks) {
    return (
      <div className="pt-2 border-t border-border">
        <p className="text-muted-foreground whitespace-pre-line">{event.descricao}</p>
      </div>
    );
  }

  const remainingLines = lines
    .filter((l) => !(/^Processo:\s+/.test(l)) && !(/^Comunicação de\s+/.test(l)))
    .join('\n').trim();

  return (
    <div className="pt-2 border-t border-border space-y-1.5">
      {processoNumero && (
        <LinkDosAutos
          numero={processoNumero}
          search={classe ? `?classe=${encodeURIComponent(classe)}` : ''}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
        >
          <FileText className="h-4 w-4 shrink-0" />
          <span className="underline underline-offset-2">Processo: {processoNumero}</span>
        </LinkDosAutos>
      )}
      {comLine && (event.comunicacao_id ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-0 text-muted-foreground hover:text-primary hover:bg-transparent gap-2 font-normal text-sm"
          onClick={() => onOpenComunicacao(event.comunicacao_id!)}
        >
          <Bell className="h-4 w-4 shrink-0" />
          <span className="underline underline-offset-2">{comLine}</span>
        </Button>
      ) : (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Bell className="h-4 w-4 shrink-0" />
          {comLine}
        </p>
      ))}
      {remainingLines && (
        <p className="text-muted-foreground whitespace-pre-line">{remainingLines}</p>
      )}
    </div>
  );
}
